import React, { useEffect, useMemo, useState } from "react";
import { Receipt, CheckCircle2, Clock } from "lucide-react";
import { portalApi, rupiah, STATUS_LABEL, STATUS_STYLE } from "./portalApi";
import { PortalPayModal } from "./PortalPayModal";
import { usePortalAuth } from "./PortalAuthContext";

export const PortalTagihanPage = () => {
  const { refresh } = usePortalAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tahun, setTahun] = useState("ALL");
  const [payInv, setPayInv] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setItems((await portalApi.get("/tagihan")).data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const years = useMemo(() => [...new Set(items.map((t) => String(t.periode_kode || "").slice(0, 4)).filter(Boolean))].sort().reverse(), [items]);
  const shown = tahun === "ALL" ? items : items.filter((t) => String(t.periode_kode || "").startsWith(tahun));
  const totalLunas = shown.filter((t) => t.status === "PAID").reduce((s, t) => s + Number(t.jumlah_dibayar || t.total_tagihan || 0), 0);
  const totalTunggakan = shown.reduce((s, t) => s + Number(t.sisa_tagihan || 0), 0);

  return (
    <div className="space-y-4" data-testid="portal-tagihan">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-bold text-white flex items-center gap-2"><Receipt className="w-4 h-4 text-amber-400" /> Riwayat Tagihan</h1>
        <select value={tahun} onChange={(e) => setTahun(e.target.value)} className="px-3 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-white" data-testid="portal-tagihan-tahun">
          <option value="ALL">Semua tahun</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/30"><p className="text-[10px] text-slate-400">Total dibayar</p><p className="text-base font-bold text-emerald-300 font-mono" data-testid="portal-total-lunas">{rupiah(totalLunas)}</p></div>
        <div className="p-3 rounded-xl bg-rose-500/5 border border-rose-500/30"><p className="text-[10px] text-slate-400">Tunggakan</p><p className="text-base font-bold text-rose-300 font-mono" data-testid="portal-total-tunggakan">{rupiah(totalTunggakan)}</p></div>
      </div>

      {loading ? (
        <p className="text-center text-xs text-slate-500 py-8">Memuat riwayat...</p>
      ) : shown.length === 0 ? (
        <p className="text-center text-xs text-slate-500 py-8">Belum ada tagihan.</p>
      ) : (
        <div className="space-y-2" data-testid="portal-tagihan-list">
          {shown.map((t) => (
            <div key={t.id} className="p-3.5 rounded-xl bg-[#0d1527] border border-slate-800" data-testid={`portal-tagihan-item-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-white">{t.periode_bulan}</p>
                  <p className="text-[11px] text-slate-400 font-mono">{t.no_invoice}</p>
                  <p className="text-[11px] text-slate-500 mt-1">{t.paket_nama}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-white font-mono">{rupiah(t.total_tagihan)}</p>
                  <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded border ${STATUS_STYLE[t.status] || STATUS_STYLE.EXPIRED}`}>{STATUS_LABEL[t.status] || t.status}</span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-slate-800/80 flex items-center justify-between text-[11px]">
                {t.status === "PAID" ? (
                  <span className="text-emerald-300 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Dibayar {t.tanggal_bayar || "-"} • {t.metode_pembayaran || "-"}</span>
                ) : (
                  <span className="text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Jatuh tempo {t.jatuh_tempo}</span>
                )}
                {t.status !== "PAID" && t.status !== "CANCELLED" && (
                  <button onClick={() => setPayInv(t)} className="px-3 py-1 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 font-semibold" data-testid={`portal-bayar-${t.id}`}>Bayar</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {payInv && <PortalPayModal invoice={payInv} onClose={() => setPayInv(null)} onPaid={() => { load(); refresh(); }} />}
    </div>
  );
};
